import { debounce, routeUrl } from '@theme/utils';

/**
 * Header quick search (`<predictive-search class="gs-sbox">`, mockup `.gs-sbox` / `#gs-ac`).
 * Progressive enhancement of a native `<form action="/search">`: without JS, Enter opens the search page.
 * - empty field + focus: popular searches (chips rendered by sections/header.liquid);
 * - typing: suggestions rendered by sections/predictive-search.liquid (Predictive Search API + Section Rendering);
 * - keyboard (mockup `acKey`): ↑/↓ move through `[data-ac]` options (`aria-activedescendant`), Enter opens the
 *   active one (or submits the form), Escape closes the panel then clears the field.
 */

export const SECTION_ID = 'predictive-search';
export const DEBOUNCE_MS = 200;

/**
 * @param {string} base - `routes.predictive_search_url`
 * @param {string} query
 * @returns {string}
 */
export function suggestUrl(base, query) {
  const params = new URLSearchParams({
    q: query,
    section_id: SECTION_ID,
    'resources[type]': 'product,collection',
    'resources[limit]': '5',
    'resources[options][unavailable_products]': 'last',
  });
  return `${base}?${params}`;
}

/**
 * Next active option index, wrapping around (mockup `acKey`).
 *
 * @param {number} current - -1 when nothing is active.
 * @param {number} count
 * @param {1 | -1} direction
 * @returns {number}
 */
export function moveIndex(current, count, direction) {
  if (count <= 0) return -1;
  if (direction > 0) return (current + 1) % count;
  return current <= 0 ? count - 1 : current - 1;
}

/**
 * Inner HTML of the section in a Section Rendering API response.
 *
 * @param {string} html
 * @returns {string}
 */
export function parseSection(html) {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return (doc.getElementById(`shopify-section-${SECTION_ID}`) ?? doc.body).innerHTML.trim();
}

export class PredictiveSearch extends HTMLElement {
  /** @type {Map<string, string>} */
  #cache = new Map();
  /** @type {AbortController | undefined} */
  #request;
  /** @type {AbortController | undefined} */
  #listeners;
  #query = '';
  #index = -1;

  #debouncedFetch = debounce((/** @type {string} */ q) => this.#fetch(q), DEBOUNCE_MS);

  connectedCallback() {
    this.#listeners = new AbortController();
    const signal = this.#listeners.signal;
    this.addEventListener('focusin', this.#onFocusIn, { signal });
    this.addEventListener('focusout', this.#onFocusOut, { signal });
    this.addEventListener('input', this.#onInput, { signal });
    this.addEventListener('keydown', this.#onKeyDown, { signal });
    this.addEventListener('mousedown', this.#onMouseDown, { signal });
    this.addEventListener('mouseover', this.#onMouseOver, { signal });
    this.addEventListener('click', this.#onClick, { signal });
  }

  disconnectedCallback() {
    this.#listeners?.abort();
    this.#request?.abort();
  }

  get input() {
    return /** @type {HTMLInputElement | null} */ (this.querySelector('input[role="combobox"]'));
  }

  get panel() {
    return /** @type {HTMLElement | null} */ (this.querySelector('[data-ps-panel]'));
  }

  get results() {
    return /** @type {HTMLElement | null} */ (this.querySelector('[data-ps-results]'));
  }

  get popular() {
    return /** @type {HTMLElement | null} */ (this.querySelector('[data-ps-popular]'));
  }

  get isOpen() {
    return this.panel?.hidden === false;
  }

  /** @returns {HTMLElement[]} */
  get options() {
    return [...(this.results?.querySelectorAll('[data-ac]') ?? [])].filter((el) => el instanceof HTMLElement);
  }

  open() {
    const { panel, input } = this;
    if (!panel || !input) return;
    // Nothing to show for an empty field without popular searches.
    if (!this.#query && !this.popular) return this.close();
    // First suggestions still loading: no empty panel, #render opens it.
    if (this.#query && !this.results?.childElementCount) return this.close();
    panel.hidden = false;
    input.setAttribute('aria-expanded', 'true');
  }

  close() {
    if (this.panel) this.panel.hidden = true;
    this.input?.setAttribute('aria-expanded', 'false');
    this.#setActive(-1);
  }

  /** Syncs the panel with the field value. */
  update() {
    const input = this.input;
    if (!input) return;
    this.#query = input.value.trim();
    input.closest('.gs-search')?.classList.toggle('has', input.value !== '');
    const { popular, results } = this;
    if (popular) popular.hidden = this.#query !== '';
    if (results) results.hidden = this.#query === '';
    this.#setActive(-1);
    if (!this.#query) {
      this.#request?.abort();
      if (results) results.innerHTML = '';
      this.#announce('');
    } else if (this.#cache.has(this.#query)) {
      this.#render(this.#cache.get(this.#query) ?? '');
    } else {
      this.#debouncedFetch(this.#query);
    }
    this.open();
  }

  /** @param {string} query */
  async #fetch(query) {
    if (query !== this.#query) return;
    this.#request?.abort();
    const request = new AbortController();
    this.#request = request;
    try {
      const response = await fetch(suggestUrl(this.dataset.url || routeUrl('search/suggest'), query), {
        signal: request.signal,
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const html = parseSection(await response.text());
      this.#cache.set(query, html);
      if (query === this.#query) this.#render(html);
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      console.error(error);
      // Fall back to the search page: Enter still submits the form.
      if (query === this.#query && this.results) this.results.innerHTML = '';
    }
  }

  /** @param {string} html */
  #render(html) {
    const results = this.results;
    if (!results) return;
    results.innerHTML = html;
    if (document.activeElement === this.input) this.open();
    this.#setActive(-1);
    const summary = results.querySelector('[data-ps-summary]');
    this.#announce(summary instanceof HTMLElement ? (summary.dataset.psSummary ?? '') : '');
  }

  /** @param {string} message */
  #announce(message) {
    const status = this.querySelector('[data-ps-status]');
    if (status) status.textContent = message;
  }

  /** @param {number} index */
  #setActive(index) {
    const options = this.options;
    this.#index = index >= 0 && index < options.length ? index : -1;
    options.forEach((option, i) => {
      const active = i === this.#index;
      option.classList.toggle('act', active);
      option.setAttribute('aria-selected', String(active));
    });
    const active = options[this.#index];
    if (active) {
      this.input?.setAttribute('aria-activedescendant', active.id);
      active.scrollIntoView?.({ block: 'nearest' });
    } else {
      this.input?.removeAttribute('aria-activedescendant');
    }
  }

  /** @param {FocusEvent} event */
  #onFocusIn = (event) => {
    if (event.target === this.input && !this.isOpen) this.update();
  };

  /** @param {FocusEvent} event */
  #onFocusOut = (event) => {
    if (event.relatedTarget instanceof Node && this.contains(event.relatedTarget)) return;
    this.close();
  };

  /** @param {Event} event */
  #onInput = (event) => {
    if (event.target === this.input) this.update();
  };

  /** @param {KeyboardEvent} event */
  #onKeyDown = (event) => {
    if (event.target !== this.input) return;
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp': {
        if (!this.#query) return;
        event.preventDefault();
        if (!this.isOpen) this.open();
        this.#setActive(moveIndex(this.#index, this.options.length, event.key === 'ArrowDown' ? 1 : -1));
        break;
      }
      case 'Enter': {
        const active = this.isOpen ? this.options[this.#index] : undefined;
        if (active) {
          event.preventDefault();
          active.click();
        }
        break; // otherwise the native form submits to the search page
      }
      case 'Escape': {
        const input = this.input;
        if (this.isOpen) {
          event.preventDefault();
          this.close();
        } else if (input?.value) {
          event.preventDefault();
          input.value = '';
          this.#query = '';
          input.closest('.gs-search')?.classList.remove('has');
        }
        break;
      }
    }
  };

  /** Keeps the focus in the field while clicking inside the panel (links still navigate on click). */
  #onMouseDown = (/** @type {MouseEvent} */ event) => {
    if (event.target instanceof Node && this.panel?.contains(event.target)) event.preventDefault();
  };

  /** @param {MouseEvent} event */
  #onMouseOver = (event) => {
    const option = event.target instanceof Element ? event.target.closest('[data-ac]') : null;
    if (option instanceof HTMLElement) this.#setActive(this.options.indexOf(option));
  };

  /** Popular search chip: fills the field and searches. @param {MouseEvent} event */
  #onClick = (event) => {
    const chip = event.target instanceof Element ? event.target.closest('[data-ps-term]') : null;
    const input = this.input;
    if (!(chip instanceof HTMLElement) || !input) return;
    event.preventDefault();
    input.value = chip.dataset.psTerm ?? '';
    input.focus();
    this.update();
  };
}

if (!customElements.get('predictive-search')) customElements.define('predictive-search', PredictiveSearch);
